(() => {
  const init = () => {
    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canObserve = 'IntersectionObserver' in window;

    document.querySelectorAll('[data-ps-hero]').forEach((hero) => {
      requestAnimationFrame(() => hero.classList.add('is-ready'));
    });

    const sections = [...document.querySelectorAll('.ptv-service:not([data-ps-hero])')];
    if (!reduceMotion && canObserve) {
      sections.forEach((section) => section.classList.add('is-section-pending'));
      const sectionObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove('is-section-pending');
          entry.target.classList.add('is-section-visible');
          sectionObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -4%', threshold: 0.04 });
      sections.forEach((section) => sectionObserver.observe(section));
    } else {
      sections.forEach((section) => section.classList.add('is-section-visible'));
    }

    const choreographedSections = [...document.querySelectorAll('[data-ps-calibration-definition], [data-ps-calibration-factory]')];
    const activateDefinitionTopics = (section) => {
      const topics = section.querySelector('[data-ps-definition-topics]');
      const words = topics ? [...topics.children] : [];
      if (!words.length || reduceMotion) return;
      topics.classList.add('has-topic-motion');
      words.forEach((word, index) => setTimeout(() => {
        words.forEach((item) => item.classList.remove('is-active'));
        word.classList.add('is-active');
      }, 180 + index * 150));
    };
    if (!reduceMotion && canObserve) {
      choreographedSections.forEach((section) => section.classList.add('has-choreo'));
      const choreographyObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-choreo-visible');
          activateDefinitionTopics(entry.target);
          choreographyObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8%', threshold: 0.08 });
      choreographedSections.forEach((section) => choreographyObserver.observe(section));
    } else {
      choreographedSections.forEach((section) => section.classList.add('is-choreo-visible'));
    }

    document.querySelectorAll('[data-ps-calibration-factory]').forEach((factory) => {
      const sentinel = factory.querySelector('[data-ps-factory-sentinel]');
      const mobile = matchMedia('(max-width: 767px)').matches;
      if (!sentinel || reduceMotion || !canObserve || mobile) {
        factory.classList.add('is-reference-mode');
        return;
      }
      const factoryObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          factory.classList.toggle('is-reference-mode', entry.boundingClientRect.top < innerHeight * 0.55);
        });
      }, { rootMargin: '-45% 0px -45%', threshold: 0 });
      factoryObserver.observe(sentinel);
    });

    const reveals = [...document.querySelectorAll('.ptv-service__reveal')]
      .filter((element) => !element.closest('[data-ps-hero]'));
    if (!reduceMotion && canObserve) {
      reveals.forEach((element) => element.classList.add('is-pending'));
      const revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.remove('is-pending');
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -6%', threshold: 0.06 });
      reveals.forEach((element) => revealObserver.observe(element));
    } else {
      reveals.forEach((element) => element.classList.add('is-visible'));
    }

    document.querySelectorAll('[data-ps-focus-list]').forEach((list) => {
      const items = [...list.querySelectorAll('[data-ps-focus-item]')];
      if (!items.length || reduceMotion || !canObserve) return;
      const setFocus = (item) => {
        items.forEach((candidate) => candidate.classList.toggle('is-focus-active', candidate === item));
        list.style.setProperty('--ps-focus-y', `${item.offsetTop + Math.min(item.offsetHeight / 2, 42) - 4}px`);
      };
      list.classList.add('has-focus-motion');
      setFocus(items[0]);
      const focusObserver = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (!visible.length) return;
        visible.sort((a, b) => Math.abs(a.boundingClientRect.top - innerHeight / 2) - Math.abs(b.boundingClientRect.top - innerHeight / 2));
        setFocus(visible[0].target);
      }, { rootMargin: '-34% 0px -34%', threshold: 0 });
      items.forEach((item) => focusObserver.observe(item));
    });

    document.querySelectorAll('[data-ps-process]').forEach((process) => {
      const steps = [...process.querySelectorAll('[data-ps-step]')];
      if (!steps.length) return;

      const setActive = (activeIndex) => {
        steps.forEach((step, index) => {
          step.classList.toggle('is-active', index === activeIndex);
          step.classList.toggle('is-past', index < activeIndex);
        });
        const progress = steps.length > 1 ? activeIndex / (steps.length - 1) : 1;
        process.style.setProperty('--ps-progress', String(progress));
      };

      if (reduceMotion || !canObserve) {
        steps.forEach((step) => step.classList.add('is-active'));
        process.style.setProperty('--ps-progress', '1');
        return;
      }

      process.classList.add('has-motion');
      setActive(0);
      const processObserver = new IntersectionObserver((entries) => {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (!visible.length) return;
        visible.sort((a, b) => Math.abs(a.boundingClientRect.top - innerHeight / 2) - Math.abs(b.boundingClientRect.top - innerHeight / 2));
        setActive(steps.indexOf(visible[0].target));
      }, { rootMargin: '-36% 0px -36%', threshold: 0 });
      steps.forEach((step) => processObserver.observe(step));
    });

    document.querySelectorAll('[data-ps-technical-strip]').forEach((strip) => {
      if (reduceMotion || !canObserve) {
        strip.classList.add('is-visible');
        return;
      }
      strip.classList.add('has-motion');
      const stripObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          strip.classList.add('is-visible');
          stripObserver.unobserve(strip);
        });
      }, { threshold: 0.18 });
      stripObserver.observe(strip);
    });

    document.querySelectorAll('[data-ps-before-after]').forEach((stage) => {
      const range = stage.querySelector('input[type="range"]');
      if (!range) return;
      const update = () => {
        stage.style.setProperty('--ps-split', `${range.value}%`);
        range.setAttribute('aria-valuetext', `Pozycja podziału: ${range.value} procent`);
      };
      range.addEventListener('input', update);
      update();
    });

    document.querySelectorAll('.ptv-service-faq details').forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;
      const sync = () => summary.setAttribute('aria-expanded', String(details.open));
      details.addEventListener('toggle', sync);
      sync();
    });

    document.querySelectorAll('[data-ps-scroll]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = document.querySelector(link.getAttribute('href'));
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });
  };

  document.readyState === 'loading' ? addEventListener('DOMContentLoaded', init) : init();
})();
